import React, { Dispatch, ReactElement, SetStateAction } from 'react';
import { DataObject } from '../../../../types/redux/data/dataTypes';
import { CustomColumn } from '../../../../types/components/tables/tableTypes';
import GridItem from '../../../layout/GridComponents/GridItem';
import CustomTable from '../../../tables/CustomTable';
import FillCell from '../../../tables/FillCell';
import { mainColors } from '../../../../styling/theme';
import { addCommasToNumbersAndRound } from '../../../../utilities/numberFormatters';
import { useSelector } from 'react-redux';
import { createSectionFundsSelector } from '../../../../redux/pages/selectors';
import { Typography } from '@mui/material';
import {
  FundAssetLiquidationTimeData,
  FundData,
  ScenarioSummary,
} from './fund-asset-liquidation.types';

export type FALTDisplayOptions = 'Scenario1' | 'Scenario2' | 'Scenario3';

interface FundAssetLiquidationTableProps {
  activeFund: string;
  stressLevel: string;
  chosenDisplay: FALTDisplayOptions;
  fundAssetData: FundAssetLiquidationTimeData;
  stressLevelOptions: any;
  isFetching: boolean;
}

interface StressSelectProps {
  stressLevel: string;
  setStressLevel: Dispatch<SetStateAction<string>>;
}
export interface BasicLiquidityData {
  zeroToOne: number;
  twoToSeven: number;
  eightToThirty: number;
  thirtyOneToNinety: number;
  ninetyOneToOneEighty: number;
  OneEightOneToThreeSixFour: number;
  moreThanAYear: number;
}
export type StressLevel =
  | 'standard'
  | 'stressed'
  | 'superStressed'
  | 'euSovDebtCrisisJul11'
  | 'creditCrisisSept2008';
export interface AggregateLiquidityData extends BasicLiquidityData {
  assetName: string;
  [key: string]: any;
  isin?: string;
  grossExposure: string | number;
  itemNumber?: number;
  stressLevel: string;
  LiquidationDays90Percent?: number | string;
  LiquidationDays100Percent?: number | string;
}
export const stressLevelMap = {
  standard: 'Standard',
  stressed: 'Stressed',
  superStressed: 'Super Stressed',
  euSovDebtCrisisJul11: 'EU Sov Debt Crisis Jul11',
  creditCrisisSept2008: 'Credit Crisis Sept08',
};
export interface IFundAssetLiquidityData extends AggregateLiquidityData {
  position: string | number;
  headerRow: boolean;
  scenario: string;
}

export const csvFundAssetLiquidationTime = [
  'assetName',
  'isin',
  'position',
  'LiquidationDays90Percent',
  'LiquidationDays100Percent',
  'grossExposure',
  'zeroToOne',
  'twoToSeven',
  'eightToThirty',
  'thirtyOneToNinety',
  'ninetyOneToOneEighty',
  'OneEightOneToThreeSixFour',
  'moreThanAYear',
  'LiquidationDays90Percent',
  'LiquidationDays100Percent',
];

export const fundAssetSummaryColumnsBasic: CustomColumn<IFundAssetLiquidityData>[] =
  [
    {
      title: 'Gross Exposure',
      field: 'grossExposure',
      cellStyle: {
        textAlign: 'center',
        minWidth: '31rem',
      },
    },
    {
      title: '0 - 1',
      field: 'zeroToOne',
      width: '9.38rem',
      cellStyle: {
        padding: 0,
      },
    },
    {
      title: '2 - 7',
      field: 'twoToSeven',
      width: '9.38rem',
      cellStyle: {
        padding: 0,
      },
    },
    {
      title: '8 - 30',
      field: 'eightToThirty',
      width: '9.38rem',
      cellStyle: {
        padding: 0,
      },
    },
    {
      title: '31 - 90',
      field: 'thirtyOneToNinety',
      width: '9.38rem',
      cellStyle: {
        padding: 0,
      },
    },
    {
      title: '91 - 180',
      field: 'ninetyOneToOneEighty',
      width: '9.38rem',
      cellStyle: {
        padding: 0,
      },
    },
    {
      title: '180 - 364',
      field: 'OneEightOneToThreeSixFour',
      width: '9.38rem',
      cellStyle: {
        padding: 0,
      },
    },
    {
      title: '> 365',
      field: 'moreThanAYear',
      width: '9.38rem',
      cellStyle: {
        padding: 0,
      },
    },
  ];
export const fundAssetSummaryColumnsBusinessDays: CustomColumn<IFundAssetLiquidityData>[] =
  [
    {
      title: 'Gross Exposure',
      field: 'grossExposure',
      cellStyle: {
        textAlign: 'center',
        minWidth: '13rem',
      },
    },
    {
      title: 'Business Days to Liquidate 90%',
      field: 'LiquidationDays90Percent',
      cellStyle: {
        textAlign: 'center',
        minWidth: '6rem',
      },
    },
    {
      title: 'Business Days to Liquidate 100%',
      field: 'LiquidationDays100Percent',
      cellStyle: {
        textAlign: 'center',
        minWidth: '6rem',
      },
    },
    {
      title: '0 - 1',
      field: 'zeroToOne',
      width: '9.38rem',
      cellStyle: {
        padding: 0,
      },
    },
    {
      title: '2 - 7',
      field: 'twoToSeven',
      width: '9.38rem',
      cellStyle: {
        padding: 0,
      },
    },
    {
      title: '8 - 30',
      field: 'eightToThirty',
      width: '9.38rem',
      cellStyle: {
        padding: 0,
      },
    },
    {
      title: '31 - 90',
      field: 'thirtyOneToNinety',
      width: '9.38rem',
      cellStyle: {
        padding: 0,
      },
    },
    {
      title: '91 - 180',
      field: 'ninetyOneToOneEighty',
      width: '9.38rem',
      cellStyle: {
        padding: 0,
      },
    },
    {
      title: '180 - 364',
      field: 'OneEightOneToThreeSixFour',
      width: '9.38rem',
      cellStyle: {
        padding: 0,
      },
    },
    {
      title: '> 365',
      field: 'moreThanAYear',
      width: '9.38rem',
      cellStyle: {
        padding: 0,
      },
    },
  ];

export const fundAssetColumns: CustomColumn<IFundAssetLiquidityData>[] = [
  {
    title: 'Asset Name',
    field: 'assetName',
    cellStyle: {
      padding: 0,
      paddingLeft: '0.62rem',
    },
  },
  {
    title: 'ISIN',
    field: 'isin',
    cellStyle: {
      textAlign: 'center',
      padding: 0,
      paddingRight: '0.62rem',
    },
  },
  {
    title: 'Position',
    field: 'position',
    cellStyle: {
      textAlign: 'center',
      padding: 0,
      paddingRight: '0.62rem',
    },
  },
  {
    title: 'Gross Exposure',
    field: 'grossExposure',
    cellStyle: {
      textAlign: 'center',
      padding: 0,
      paddingRight: '0.62rem',
    },
  },
  {
    title: '0 - 1',
    field: 'zeroToOne',
    width: '9.38rem',
    cellStyle: {
      padding: 0,
      textAlign: 'center',
    },
  },
  {
    title: '2 - 7',
    field: 'twoToSeven',
    width: '9.38rem',
    cellStyle: {
      padding: 0,
      textAlign: 'center',
    },
  },
  {
    title: '8 - 30',
    field: 'eightToThirty',
    width: '9.38rem',
    cellStyle: {
      padding: 0,
      textAlign: 'center',
    },
  },
  {
    title: '31 - 90',
    field: 'thirtyOneToNinety',
    width: '9.38rem',
    cellStyle: {
      padding: 0,
      textAlign: 'center',
    },
  },
  {
    title: '91 - 180',
    field: 'ninetyOneToOneEighty',
    width: '9.38rem',
    cellStyle: {
      padding: 0,
      textAlign: 'center',
    },
  },
  {
    title: '180 - 364',
    field: 'OneEightOneToThreeSixFour',
    width: '9.38rem',
    cellStyle: {
      padding: 0,
      textAlign: 'center',
    },
  },
  {
    title: '> 365',
    field: 'moreThanAYear',
    width: '9.38rem',
    cellStyle: {
      padding: 0,
      textAlign: 'center',
    },
  },
];

export const mapStressToColor = (stressLevel: string) => {
  switch (stressLevel) {
    case 'data':
      return mainColors.mainBlue;
    case 'data_s':
      return mainColors.Fail;
    case 'data_ss':
      return mainColors.Fail_even_darker;
    default:
      return mainColors.mainBlue;
  }
};
export const createColumn: <T extends AggregateLiquidityData>(
  col: CustomColumn<T>,
  stressLevel: string,
) => CustomColumn<T> = (col, stressLevel) => {
  const isFillBoxColumn =
    col.field !== 'assetName' &&
    col.field !== 'grossExposure' &&
    col.field !== 'position' &&
    col.field !== 'LiquidationDays90Percent' &&
    col.field !== 'LiquidationDays100Percent' &&
    col.field !== 'isin';
  return {
    ...col,
    render: (rowData) => {
      const rawValue = rowData[col.field!];

      const safeValue =
        typeof rawValue === 'number' && !isNaN(rawValue) ? rawValue : null;

      if (isFillBoxColumn) {
        return safeValue !== null ? (
          <FillCell
            delayMultiplier={rowData.itemNumber}
            cellColor={mapStressToColor(stressLevel)}
            value={safeValue}
          />
        ) : (
          <span className="relative flex h-12 items-center justify-center overflow-hidden text-gray-400">
            N/A
          </span>
        );
      }

      return col.field !== 'assetName' && col.field !== 'isin'
        ? safeValue !== null
          ? addCommasToNumbersAndRound(safeValue)
          : ''
        : rowData[col.field];
    },
    pdfRenderType: isFillBoxColumn ? 'FillBox' : undefined,
    renderMethod: isFillBoxColumn
      ? {
          methodName: 'statusBox',
          params: ['itemNumber', 'stressLevel', col.field as string],
        }
      : undefined,
    headerStyle: {
      textAlign: col.field === 'assetName' ? 'left' : 'center',
    },
  };
};

export const createFundAssetColumns: (
  stressLevel: string,
) => CustomColumn<IFundAssetLiquidityData>[] = (stressLevel: string) => {
  return fundAssetColumns.map((col: CustomColumn<IFundAssetLiquidityData>) =>
    createColumn(col, stressLevel),
  );
};

export const createOverviewColumns: (
  stressLevel: string,
) => CustomColumn<IFundAssetLiquidityData>[] = (stressLevel: string) => {
  const columnsToUse = ['data', 'data_s', 'data_ss'].includes(stressLevel)
    ? fundAssetSummaryColumnsBusinessDays
    : fundAssetSummaryColumnsBasic;
  return columnsToUse.map((col: CustomColumn<IFundAssetLiquidityData>) =>
    createColumn(col, stressLevel),
  );
};



export function buildFundAssetTableData(
  data: FundAssetLiquidationTimeData,
  scenario: keyof FundAssetLiquidationTimeData,
  display: FALTDisplayOptions,
) {
  let businessDays90: any = null;
  let businessDats100: any = null;
  if (scenario === 'data') {
    businessDays90 = data['90% Liquidation Business Days'];
    businessDats100 = data['100% Liquidation Business Days'];
  } else if (scenario === 'data_s') {
    businessDays90 = data['90% Liquidation Business Days Stressed'];
    businessDats100 = data['100% Liquidation Business Days Stressed'];
  } else if (scenario === 'data_ss') {
    businessDays90 = data['90% Liquidation Business Days SuperStressed'];
    businessDats100 = data['100% Liquidation Business Days SuperStressed'];
  }

  const areaWithData = data[scenario];
  if (
    !areaWithData ||
    typeof areaWithData !== 'object' ||
    Array.isArray(areaWithData)
  ) {
    return [];
  }
  const dataKeys = Object.keys(areaWithData);

  const returnedData = dataKeys.map((dataKey) => {
    const fund = areaWithData[dataKey];

    const scenarioData = (fund as FundData | ScenarioSummary)[display] ?? [];

    if (dataKey !== 'Total') {
      const { position_size, gross_exposure, isin } = fund as FundData;

      const [
        zero_one,
        two_seven,
        eight_thirty,
        thirtyone,
        ninetyone,
        oneeightone,
        gt365,
      ] = scenarioData;

      return {
        scenario,
        stressLevel: display,
        headerRow: false,
        assetName: dataKey,
        isin: isin !== 'None' ? isin : '',
        position: position_size,
        grossExposure: Math.round(gross_exposure),
        zeroToOne: zero_one,
        twoToSeven: two_seven,
        eightToThirty: eight_thirty,
        thirtyOneToNinety: thirtyone,
        ninetyOneToOneEighty: ninetyone,
        OneEightOneToThreeSixFour: oneeightone,
        moreThanAYear: gt365,
        LiquidationDays90Percent: '',
        LiquidationDays100Percent: '',
      };
    }

    const [zero, two, eight, thirty, ninety, oneeight, gt] = scenarioData;

    return {
      scenario,
      stressLevel: display,
      headerRow: true,
      assetName: 'Total',
      position: '',
      grossExposure: data.gross_exposure,
      zeroToOne: zero,
      twoToSeven: two,
      eightToThirty: eight,
      thirtyOneToNinety: thirty,
      ninetyOneToOneEighty: ninety,
      OneEightOneToThreeSixFour: oneeight,
      moreThanAYear: gt,
      LiquidationDays90Percent: businessDays90,
      LiquidationDays100Percent: businessDats100,
    };
  });

  // Move Total row to top
  const headerIndex = returnedData.findIndex((d) => d.headerRow);
  if (headerIndex > -1) {
    const [headerRow] = returnedData.splice(headerIndex, 1);
    returnedData.unshift(headerRow);
  }

  return returnedData;
}

function FundAssetLiquidationTable({
  stressLevel,
  chosenDisplay,
  activeFund,
  fundAssetData,
  stressLevelOptions,
  isFetching,
}: FundAssetLiquidationTableProps): ReactElement | null {
  const tableData = buildFundAssetTableData(
    fundAssetData,
    stressLevel as keyof FundAssetLiquidationTimeData,
    chosenDisplay,
  );
  const overviewColumns = createOverviewColumns(stressLevel);
  const columns = createFundAssetColumns(stressLevel);
  const fundsSelector = createSectionFundsSelector('liquidity');
  const funds = useSelector(fundsSelector);

  const fundTitle =
    funds && funds.length
      ? funds.find((fund: any) => fund.id === activeFund)?.name + ' - Breakdown'
      : '';

  const getScenarioName = (value: string) => {
    const match = stressLevelOptions.find((item: any) => item.value === value);
    return match ? match.label : '';
  };

  const positionDate = fundAssetData.selected_position_date;

  return tableData.length ? (
    <>
      <GridItem xs={12} card>
        <CustomTable<IFundAssetLiquidityData>
          title="Liquidation Time (Days)"
          showToolbar={true}
          exportFileName={`${activeFund}_liquidation_time_days_aggregate_${positionDate}`}
          loading={isFetching}
          csvFields={csvFundAssetLiquidationTime}
          options={{
            exportButton: true,
            paging: false,
            sorting: false,
          }}
          data={[tableData[0]]}
          columns={overviewColumns}
          toolbarComponents={{
            toolbarTitle: fundTitle,
            titleStyle: {
              fontSize: '1.2rem',
              fontWeight: 500,
            },
          }}
        />
      </GridItem>
      <GridItem xs={12} card>
        <CustomTable<IFundAssetLiquidityData>
          title="Liquidation Time (Days)"
          showToolbar={true}
          exportFileName={`${activeFund}_liquidation_time_days_${positionDate}`}
          noteExport={{
            fundId: activeFund,
            subject: 'ucits_fund-asset-liquidation-time',
            topic: 'Notes',
            positionDate,
            shouldCall: false,
          }}
          loading={isFetching}
          csvFields={csvFundAssetLiquidationTime}
          options={{
            exportButton: true,
            paging: tableData.length > 50,
            pageSize: 50,
            pageSizeOptions: [50, 100, 200, 500],
            search: true,
            emptyRowsWhenPaging: false,
          }}
          data={tableData.filter((el, index) => index !== 0)}
          toolbarComponents={{
            toolbarTitle: fundTitle,
            titleStyle: {
              fontSize: '1.2rem',
              fontWeight: 500,
            },
          }}
          additionalDetailForPdf={
            stressLevel === 'data'
              ? 'Stress Level: BAU'
              : `Stress Level: ${getScenarioName(stressLevel)}`
          }
          columns={columns}
        />
      </GridItem>
    </>
  ) : null;
}

export default FundAssetLiquidationTable;